import ScButton from "@/src/components/ui-kit/button";
import { useTheme } from "@/src/styles/theme-provider";
import { CloseCircle } from "iconsax-react-nativejs";
import React from "react";
import { useTranslation } from "react-i18next";
import { Modal, Pressable, Text, TextInput, View } from "react-native";

type ConfirmModalProps = Readonly<{
  visible: boolean;
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
  confirmationText?: string;
  confirmationLabel?: string;
  confirmationLabelKey?: string;
  confirmationLabelParams?: Record<string, string>;
}>;

const ConfirmModal = ({
  visible,
  title,
  description,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  loading = false,
  confirmationText,
  confirmationLabelKey,
  confirmationLabelParams,
}: ConfirmModalProps) => {
  const { themeVars, varStyle } = useTheme();
  const { t } = useTranslation();
  const [value, setValue] = React.useState("");
  const requiresConfirmation = Boolean(confirmationText);
  const isConfirmDisabled = requiresConfirmation && value !== confirmationText;

  return (
    <Modal
      transparent
      animationType="fade"
      visible={visible}
      onRequestClose={loading ? undefined : onCancel}
    >
      <Pressable
        style={varStyle}
        className="flex-1 justify-center items-center bg-shadow-dark px-6"
        onPress={loading ? undefined : onCancel}
      >
        <Pressable
          className="w-full max-w-md bg-ash rounded-2xl p-6 gap-4"
          onPress={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <View className="flex-row justify-between items-center">
            <Text className="flex-1 text-xl font-semibold text-onyx">
              {t(title)}
            </Text>
            <Pressable
              className="p-1.5 rounded-full hover:bg-ash-3"
              onPress={loading ? undefined : onCancel}
            >
              <CloseCircle color={themeVars["--color-primary-default"]} />
            </Pressable>
          </View>

          {/* Description */}
          {description && (
            <Text className="text-center text-slate">{t(description)}</Text>
          )}

          {/* Confirmation input */}
          {confirmationText && (
            <View className="gap-2">
              <Text
                selectable
                className="text-sm text-center font-medium text-crimson"
              >
                {confirmationLabelKey
                  ? t(confirmationLabelKey, confirmationLabelParams)
                  : t("HINT_CONFIRMATION_DEFAULT")}
              </Text>
              <TextInput
                value={value}
                underlineColorAndroid="transparent"
                onChangeText={setValue}
                placeholder={confirmationText}
                placeholderTextColor={themeVars["--color-slate-default"]}
                style={{
                  borderColor: themeVars["--color-ash-3"],
                  color: themeVars["--color-onyx-default"],
                }}
                className="border rounded-lg px-3 py-2"
              />
            </View>
          )}

          {/* Actions */}
          <View className="flex-row justify-center gap-10 mt-4">
            <ScButton
              text={cancelText || ""}
              onPress={onCancel}
              mode="outlined"
              disabled={loading}
            />
            <ScButton
              text={confirmText || ""}
              onPress={onConfirm}
              disabled={isConfirmDisabled}
              loading={loading}
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

export default ConfirmModal;
